import React, { useState, useEffect, useRef } from 'react';
import { createMeeting, updateMeeting } from '../../api/meetings';
import { toast } from 'react-hot-toast';
import { FiX, FiSearch } from 'react-icons/fi';
import { api } from '../../finance/api/client';

const generateTimeOptions = () => {
  const options = [];
  for(let h=0; h<24; h++) {
    const hr = h.toString().padStart(2, '0');
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayHr = h % 12 || 12;
    options.push({ value: `${hr}:00`, label: `${displayHr}:00 ${ampm}` });
    options.push({ value: `${hr}:30`, label: `${displayHr}:30 ${ampm}` });
  }
  return options;
};
const timeOptions = generateTimeOptions();

const TimeSelect = ({ value, onChange, name, placeholder, startTime }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedLabel = timeOptions.find(t => t.value === value)?.label || placeholder;

  const getDuration = (start, end) => {
    if (!start || !end) return null;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const diffMin = (eh * 60 + em) - (sh * 60 + sm);
    if (diffMin <= 0) return null;
    
    const h = Math.floor(diffMin / 60);
    const m = diffMin % 60;
    if (h === 0) return `${m}mins`;
    if (m === 0) return h === 1 ? '1 hr' : `${h} hrs`;
    return `${h} hr ${m} mins`;
  };

  return (
    <div ref={ref} className="relative w-full">
      <div onClick={() => setIsOpen(!isOpen)} className="w-full px-2 py-2 bg-slate-50 border-b-2 border-transparent hover:bg-slate-100 focus:bg-white focus:border-blue-600 outline-none transition-all text-sm rounded-t-md cursor-pointer flex justify-between items-center gap-2">
        <span className="whitespace-nowrap truncate">{selectedLabel}</span>
        <svg className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
      </div>
      {isOpen && (
        <div className="absolute z-50 min-w-full w-max mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-40 overflow-y-auto custom-scrollbar">
          {timeOptions.map((t, index) => {
            const duration = startTime ? getDuration(startTime, t.value) : null;
            return (
              <div key={t.value} onClick={() => { onChange({ target: { name, value: t.value } }); setIsOpen(false); }}
                   className={`px-3 py-2 text-sm cursor-pointer transition-colors hover:bg-blue-50 hover:text-slate-800 ${value === t.value ? 'bg-blue-100 text-blue-700 font-medium' : (index % 2 === 0 ? 'bg-white text-slate-700' : 'bg-slate-50 text-slate-700')} flex justify-between items-center gap-4`}>
                <span className="whitespace-nowrap">{t.label}</span>
                {duration && <span className="text-xs opacity-60 font-normal whitespace-nowrap">{duration}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const MeetingFormModal = ({ isOpen, onClose, meeting, initialDate, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    meetingType: 'INTERNAL',
    meetingMode: 'ONLINE',
    date: '',
    startTime: '',
    endTime: '',
    location: '',
    meetingLink: '',
    attendeeIds: [],
    departments: [],
    inviteAll: false
  });
  
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState(null);

  const availableDepts = ["HR", "SALES", "TECH", "PRODUCTION", "FINANCE", "SOCIAL"];

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      if (meeting) {
        setFormData({
          title: meeting.title || '',
          description: meeting.description || '',
          meetingType: meeting.meetingType || 'INTERNAL',
          meetingMode: meeting.meetingMode || 'ONLINE',
          date: meeting.meetingStart ? meeting.meetingStart.substring(0, 10) : '',
          startTime: meeting.meetingStart ? meeting.meetingStart.substring(11, 16) : '',
          endTime: meeting.meetingEnd ? meeting.meetingEnd.substring(11, 16) : '',
          location: meeting.location || '',
          meetingLink: meeting.meetingLink || '',
          attendeeIds: meeting.attendees ? meeting.attendees.map(a => a.id) : [],
          departments: meeting.departments || [],
          inviteAll: meeting.inviteAll || false
        });
      } else if (initialDate) {
        // Preset date and time based on initialDate
        const start = new Date(initialDate);
        const tzOffset = start.getTimezoneOffset() * 60000;
        const localStart = new Date(start - tzOffset);
        
        const end = new Date(localStart);
        end.setMinutes(end.getMinutes() + 30); // Default 30 min meeting

        setFormData(prev => ({
          ...prev,
          date: localStart.toISOString().substring(0, 10),
          startTime: localStart.toISOString().substring(11, 16),
          endTime: end.toISOString().substring(11, 16)
        }));
      } else {
        setFormData({
          title: '',
          description: '',
          meetingType: 'INTERNAL',
          meetingMode: 'ONLINE',
          date: '',
          startTime: '',
          endTime: '',
          location: '',
          meetingLink: '',
          attendeeIds: [],
          departments: [],
          inviteAll: false
        });
      }
    }
  }, [isOpen, meeting, initialDate]);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/system/users');
      setUsers(res.data);
    } catch (e) {
      console.warn("Could not fetch users", e);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleAttendeeToggle = (userId) => {
    setFormData(prev => {
      const isSelected = prev.attendeeIds.includes(userId);
      if (isSelected) {
        return { ...prev, attendeeIds: prev.attendeeIds.filter(id => id !== userId) };
      } else {
        return { ...prev, attendeeIds: [...prev.attendeeIds, userId] };
      }
    });
  };

  const handleDeptToggle = (dept) => {
    setFormData(prev => {
      const isSelected = prev.departments.includes(dept);
      if (isSelected) {
        return { ...prev, departments: prev.departments.filter(d => d !== dept) };
      } else {
        return { ...prev, departments: [...prev.departments, dept] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFormError(null);
    
    try {
      if (!formData.startTime || !formData.endTime) {
        setFormError("Start time and end time are required.");
        setLoading(false);
        return;
      }

      const [sh, sm] = formData.startTime.split(':').map(Number);
      const [eh, em] = formData.endTime.split(':').map(Number);
      if ((eh * 60 + em) - (sh * 60 + sm) <= 0) {
        setFormError("Meeting start time must be before end time  ");
        setLoading(false);
        return;
      }

      const payload = {
        ...formData,
        meetingStart: `${formData.date}T${formData.startTime}:00`,
        meetingEnd: `${formData.date}T${formData.endTime}:00`
      };
      
      // Remove UI-only fields
      delete payload.date;
      delete payload.startTime;
      delete payload.endTime;
      
      if (meeting) {
        await updateMeeting(meeting.id, payload);
        toast.success("Meeting updated!");
      } else {
        await createMeeting(payload);
        toast.success("Meeting created!");
      }
      onSuccess();
      onClose();
    } catch (e) {
      const errMsg = e.response?.data?.message || e.message || "Failed to save meeting. Please try again.";
      setFormError(errMsg);
      toast.error(errMsg);
      console.warn("Save meeting error", e);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const filteredUsers = users.filter(u => 
    u.username.toLowerCase().includes(searchQuery.toLowerCase()) || 
    (u.department && u.department.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="flex justify-between items-center p-6 border-b border-slate-100 bg-slate-50">
          <h2 className="text-xl font-bold text-slate-800">{meeting ? 'Edit Meeting' : 'New Meeting'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-800 p-2 rounded-full hover:bg-slate-200 transition-colors">
            <FiX size={20} />
          </button>
        </div>
        
        <div className="overflow-y-auto p-6 custom-scrollbar flex-1 bg-white">
          <form id="meetingForm" onSubmit={handleSubmit} className="space-y-6">
            
            {/* Header style inputs */}
            <div className="space-y-4 pb-4 border-b border-slate-100">
              <div>
                <input type="text" name="title" required value={formData.title} onChange={handleChange} 
                       placeholder="Add a title"
                       className="w-full px-0 py-2 bg-transparent border-none text-2xl font-bold text-slate-800 placeholder:font-normal placeholder-slate-400 focus:outline-none focus:ring-0 transition-all" />
              </div>
            </div>
            
            <div className="space-y-8">
              {/* Timing Row */}
              <div>
                <div className="flex gap-4 items-end max-w-2xl">
                  <div className="flex-[2]">
                    <label className="block text-xs font-semibold text-slate-500 mb-2">Start date</label>
                    <input type="date" name="date" required value={formData.date} onChange={handleChange} 
                           className="w-full px-3 py-2 bg-slate-50 border-b-2 border-transparent hover:bg-slate-100 focus:bg-white focus:border-blue-600 focus:outline-none transition-all text-sm rounded-t-md" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-semibold text-slate-500 mb-2">Start time</label>
                    <TimeSelect name="startTime" value={formData.startTime} onChange={handleChange} placeholder="Start" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-semibold text-slate-500 mb-2">End time</label>
                    <TimeSelect name="endTime" value={formData.endTime} onChange={handleChange} placeholder="End" startTime={formData.startTime} />
                  </div>
                </div>
              </div>

              {/* Format and Location Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Format</label>
                  <div className="flex gap-4">
                    <select name="meetingType" value={formData.meetingType} onChange={handleChange} 
                            className="flex-1 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-sm">
                      <option value="INTERNAL">Internal</option>
                      <option value="CLIENT">Client</option>
                    </select>
                    <select name="meetingMode" value={formData.meetingMode} onChange={handleChange} 
                            className="flex-1 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-sm">
                      <option value="ONLINE">Online</option>
                      <option value="OFFLINE">Offline</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Location / Link</label>
                  {formData.meetingMode !== 'ONLINE' && (
                    <input type="text" name="location" value={formData.location} onChange={handleChange} placeholder="Physical Location"
                           className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 mb-3 text-sm" />
                  )}
                  {formData.meetingMode !== 'OFFLINE' && (
                    <input type="url" name="meetingLink" value={formData.meetingLink} onChange={handleChange} placeholder="Meeting Link (e.g., Zoom, Teams)"
                           className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-sm" />
                  )}
                </div>
              </div>
            </div>
            
            {/* Description as a large box at bottom */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Description</label>
              <textarea name="description" rows="4" value={formData.description} onChange={handleChange} 
                        placeholder="Add a description or agenda..."
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all text-sm"></textarea>
            </div>

            {/* Attendees */}
            <div className="border-t border-slate-100 pt-6">
              <div className="flex items-center justify-between mb-4">
                <label className="text-sm font-bold text-slate-800">Invite required attendees</label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600 font-medium">Invite All</span>
                  <button type="button" onClick={() => setFormData(prev => ({...prev, inviteAll: !prev.inviteAll}))}
                          className={`w-11 h-6 rounded-full transition-colors relative flex items-center ${formData.inviteAll ? 'bg-blue-600' : 'bg-slate-300'}`}>
                    <span className={`w-4 h-4 bg-white rounded-full shadow-sm absolute transition-all ${formData.inviteAll ? 'left-6' : 'left-1'}`} />
                  </button>
                </div>
              </div>

              {!formData.inviteAll && (
                <div className="space-y-4">
                  <div className="relative border border-slate-200 rounded-xl p-2 bg-slate-50 min-h-[48px] flex flex-wrap gap-2 items-center focus-within:ring-2 focus-within:ring-blue-500/50">
                    {users.filter(u => formData.attendeeIds.includes(u.id)).map(u => (
                      <div key={u.id} className="flex items-center gap-1 bg-white border border-slate-200 rounded-full px-3 py-1 text-sm shadow-sm">
                        <span className="font-medium text-slate-700">{u.username}</span>
                        <button type="button" onClick={() => handleAttendeeToggle(u.id)} className="text-slate-400 hover:text-red-500 outline-none"><FiX size={14} /></button>
                      </div>
                    ))}
                    <input type="text" placeholder={formData.attendeeIds.length === 0 ? "Search names or departments..." : ""}
                           value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                           className="flex-1 bg-transparent min-w-[150px] outline-none text-sm px-2" />
                           
                    {searchQuery.trim() !== '' && (
                       <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-xl max-h-60 overflow-y-auto z-50 custom-scrollbar">
                          {filteredUsers.length === 0 ? (
                            <div className="p-4 text-center text-sm text-slate-500">No users found</div>
                          ) : (
                            filteredUsers.map(user => (
                              <div key={user.id} onClick={() => { handleAttendeeToggle(user.id); setSearchQuery(''); }}
                                   className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer border-b border-slate-50 last:border-0">
                                 <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs uppercase">{user.username.substring(0,2)}</div>
                                 <div>
                                   <div className="text-sm font-bold text-slate-700">{user.username}</div>
                                   <div className="text-xs text-slate-500">{user.department}</div>
                                 </div>
                              </div>
                            ))
                          )}
                       </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-2">Quick add by department</label>
                    <div className="flex flex-wrap gap-2">
                      {availableDepts.map(dept => (
                        <button type="button" key={dept} onClick={() => handleDeptToggle(dept)}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${formData.departments.includes(dept) ? 'bg-blue-50 border-blue-200 text-blue-700 shadow-sm' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                          + {dept}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>
        
        <div className="p-6 border-t border-slate-100 flex flex-col gap-4 bg-slate-50">
          {formError && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg text-sm flex items-center gap-2">
              <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>{formError}</span>
            </div>
          )}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-6 py-2.5 rounded-xl text-slate-600 font-medium hover:bg-slate-200 transition-colors">
              Cancel
            </button>
            <button type="submit" form="meetingForm" disabled={loading} className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors shadow-sm shadow-blue-500/30 disabled:opacity-50">
              {loading ? 'Saving...' : meeting ? 'Update Meeting' : 'Create Meeting'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingFormModal;
